import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { InfraPlan } from '../../models/plan/plan';
import { ListParams } from '../../models/listParams';
import { MijnOrganisatieListItem } from '../../models/user/mijnOrganisatieListItem';

import ProgressBar from '../controls/ProgressBar';
import TablePlannen from '../controls/tables/TablePlannen';
import NoActiveOrganisatieMessage from '../controls/NoActiveOrganisatieMessage';
import PlanDetail from './PlanDetail';
import FilterOverview from './FilterOverview';
import FilterPlannen from './FilterPlannen';

import { eventBus } from '../../services/event-emitter.service';
import { mapService } from '../../services/map.service';
import { userManagementService } from '../../services/user-management.service';
import { plannenService } from '../../services/plannen.service';

import dayjs from "dayjs";


interface SidebarProps {
  panelWidth: number;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  panelWidth
}) => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<string>('');
  const [planId, setPlanId] = useState<string | undefined>(undefined);
  const { id: routePlanId } = useParams(); // haalt route plan id op

  const [plans, setPlans] = useState<InfraPlan[]>([]);
  const [mijnOrganisatie, setMijnOrganisatie] = useState<MijnOrganisatieListItem | null>(null);

  const [filterData, setFilterData] = useState<any[]>([]);

  const [selectedSearch, setSelectedSearch] = useState<string>('');
  const [selectedStartDate, setSelectedStartDate] = useState<string>('');
  const [selectedEndDate, setSelectedEndDate] = useState<string>('');
  const [matchExactRange, setMatchExactRange] = useState<boolean>(false);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, any[]>>({
    status: [],
    discipline: [],
    type: [],
    organisatie: [],
    beoordeling: []
  });
  const [hasFetchedData, setHasFetchedData] = useState(false);

  useEffect(() => {
    if (routePlanId && routePlanId !== planId) {
      handleClickPlan(routePlanId);
    }
  }, [routePlanId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    eventBus.on('slideInPlan', handleClickPlan);
  }, []);

  useEffect(() => {
    // Ensure the fetch happens only once
    if (!hasFetchedData) {
      fetchPlans();
      fetchFilterData();
      setHasFetchedData(true); // Mark as fetched // hasFetchedData.current = true; // Mark as fetched
    }
  }, [hasFetchedData]);

  const refetchPlans = () => {
    setHasFetchedData(false); // Mark as fetched
  };

  /* Function to format the filters for the API request */

  const formatFilters = (filters: Record<string, any[]>): string => {
    const formattedFilters = Object.entries(filters)
      .filter(([_, values]) => values.length > 0) // Remove empty filters
      .map(([filterType, values]) => {
        let type = '';
        switch (filterType) {
          case 'status':
            type = 'planStatus';
            break;
          case 'type':
            type = 'planType';
            break;
          case 'organisatie':
            type = 'infplOrganizationId';
            break;
          case 'discipline':
            type = 'discipline';
            break;
          case 'beoordeling':
            type = 'infplBeoordeling';
            break;
          // case 'kosten':
            // kosten > 100
            // break;
          default:
            type = filterType;
            break;
        }


        // const filterString = values.map((value) => `${type} eq '${value}'`).join(" or ");
        // return `(${filterString})`;
        const filterValues = values.map((value) => `'${value}'`).join(', ');
        const filterString = `${type} in (${filterValues})`;
        return `${filterString}`;
      })
      .join(' and ');
  
    return formattedFilters;
  };

  const createParamFilter = (): string => {
    const filters: string[] = [];
  
    if (
      selectedFilters && (
        selectedFilters.status?.length > 0 ||
        selectedFilters.discipline?.length > 0 ||
        selectedFilters.type?.length > 0 ||
        selectedFilters.organisatie?.length > 0 ||
        selectedFilters.beoordeling?.length > 0
      )
    ) {
      const formattedFilter = formatFilters(selectedFilters);
      if (formattedFilter) {
        filters.push(formattedFilter);
      }
    }
  
    const hasStart = !!selectedStartDate;
    const hasEnd = !!selectedEndDate;
  
    if (hasStart || hasEnd) {
      const startDate = hasStart
        ? dayjs(selectedStartDate).format("YYYY-MM-DDT00:00:00.000[Z]")
        : null;
      const endDate = hasEnd
        ? dayjs(selectedEndDate).format("YYYY-MM-DDT23:59:59.000[Z]")
        : null;
  
      if (hasStart && hasEnd) {
        if (matchExactRange) {
          filters.push(`planStartDatum >= ${startDate}`);
          filters.push(`planEindDatum <= ${endDate}`);
        } 
        else {
          filters.push(`planStartDatum <= ${endDate}`);
          filters.push(`planEindDatum >= ${startDate}`);
        }
      } 
      else if (hasStart) {
        if (matchExactRange) {
          filters.push(`planStartDatum >= ${startDate}`);
          filters.push(`planEindDatum >= ${startDate}`);
        } 
        else {
          filters.push(`planEindDatum >= ${startDate}`);
        }
      } 
      else if (hasEnd) {
        if (matchExactRange) {
          filters.push(`planStartDatum <= ${endDate}`);
          filters.push(`planEindDatum <= ${endDate}`);
        } 
        else {
          filters.push(`planStartDatum <= ${endDate}`);
        }
      }
    }
  
    return filters.join(' and ');
  };

  const createParamFilterMetOrEnHaakjes = (): string => {
    const filters: string[] = [];
  
    if (
      selectedFilters && (
        selectedFilters.status?.length > 0 ||
        selectedFilters.discipline?.length > 0 ||
        selectedFilters.type?.length > 0 ||
        selectedFilters.organisatie?.length > 0 ||
        selectedFilters.beoordeling?.length > 0
      )
    ) {
      const formattedFilter = formatFilters(selectedFilters);
      if (formattedFilter) {
        filters.push(formattedFilter);
      }
    }
  
    const hasStart = !!selectedStartDate;
    const hasEnd = !!selectedEndDate;
  
    if (hasStart || hasEnd) {
      const startDate = hasStart
        ? dayjs(selectedStartDate).format("YYYY-MM-DDT00:00:00.000[Z]")
        : null;
      const endDate = hasEnd
        ? dayjs(selectedEndDate).format("YYYY-MM-DDT23:59:59.000[Z]")
        : null;
  
      let dateFilter = '';
  
      if (hasStart && hasEnd) {
        if (matchExactRange) {
          // planStartDatum and planEindDatum both inside the range
          dateFilter = `(planStartDatum >= '${startDate}' and planEindDatum <= '${endDate}')`;
        } else {
          // Plan overlaps with the range
          dateFilter = `(planStartDatum <= '${endDate}' and planEindDatum >= '${startDate}')`;
        }
      } else if (hasStart) {
        if (matchExactRange) {
          dateFilter = `(planStartDatum >= '${startDate}' and planEindDatum >= '${startDate}')`;
        } else {
          dateFilter = `(planStartDatum >= '${startDate}' or planEindDatum >= '${startDate}')`;
        }
      } else if (hasEnd) {
        if (matchExactRange) {
          dateFilter = `(planStartDatum <= '${endDate}' and planEindDatum <= '${endDate}')`;
        } else {
          dateFilter = `(planStartDatum <= '${endDate}' or planEindDatum <= '${endDate}')`;
        }
      }
  
      if (dateFilter) filters.push(dateFilter);
    }
  
    return filters.join(' and ');
  };

  const createParamCqlFilterOLD = (): string => {
    const filters: string[] = [];
  
    // if (selectedSearch) {
    //   filters.push(`planNaam like '%${selectedSearch}%'`);
    // }
  
    if (
      selectedFilters && (
        selectedFilters.status?.length > 0 ||
        selectedFilters.discipline?.length > 0 ||
        selectedFilters.type?.length > 0 ||
        selectedFilters.organisatie?.length > 0 ||
        selectedFilters.beoordeling?.length > 0
      )
    ) {
      const formattedFilter = formatFilters(selectedFilters);
      if (formattedFilter) {
        filters.push(formattedFilter);
      }
    }
  
    if (selectedStartDate) {
      const startDate = dayjs(selectedStartDate).format("YYYY-MM-DDT00:00:00.000[Z]");
      filters.push(`planStartDatum >= ${startDate}`);
    }
  
    if (selectedEndDate) {
      const endDate = dayjs(selectedEndDate).format("YYYY-MM-DDT23:59:59.000[Z]");
      filters.push(`planEindDatum <= ${endDate}`);
    }
  
    return filters.join(' and ');
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const storedOrganisatie = userManagementService.getStoredOrganisatie();
      if (storedOrganisatie && storedOrganisatie.id) {
        setMijnOrganisatie(storedOrganisatie);

        const params: ListParams = {
          searchText: selectedSearch,
          offset: pageIndex * pageSize, 
          limit: pageSize, 
          sortBy: sortBy ? `${sortBy} ${sortDirection}` : "", // Add sorting here
          filter: createParamFilter()
        };
                
        const data = await plannenService.getPlannen(params);
        eventBus.emit('setOgcFilter', params.searchText, params.filter);
        const modifiedDataItems = data.items.map((plan: InfraPlan, index: number) => ({
          ...plan,          // Copy the original plan properties
          id: plan.infplId,  // `plan-${index}`; // Add a unique id based on infplId or index for key
        }));

        if (modifiedDataItems.length > 0) {
          // Get all bbox from plans
          const arrBbox = modifiedDataItems.map((plan: InfraPlan) => plan.boundingBox);
          const bbox = mapService.calculatedBoundingBox(arrBbox) as number[];
          eventBus.emit('zoomInOnPlanBbox', bbox);
          // eventBus.emit('zoomInOnPlanBbox', bbox, params.searchText, params.cql_filter);
          
          // const bboxRD = [81002.2277319291, 454564.77977588197, 81664.38057003169, 454866.2470151363];
          // const bboxLatLng = mapService.convertBboxRDToLatLng(bboxRD, true) as number[];  
          // console.log('1 Converted to LatLng:', bboxLatLng);
          // const bboxBackToRD = mapService.convertBboxLatLngToRD(bboxLatLng, true) as number[];  
          // console.log('2 Converted back to RD:', bboxBackToRD);
        }
        else {
          eventBus.emit('zoomInOnPlanBbox'); // Zonder bbox behoud de huidige bbox
        }

        setPlans(modifiedDataItems); // Assuming the API response is a JSON array of plans

        setTotalCount(data.count);
        setLoading(false);
      }
      else {
        setMijnOrganisatie(null);
        setPlans([]);
        setTotalCount(0);
        setLoading(false);
      }
    }
    catch (error: any) {
      // If token refresh failed, redirect to the logout page
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }

      setError(error.message || 'Er is een fout opgetreden');
      setLoading(false);
    }
  };

  const fetchFilterData = async () => {
    try {
      const filters = await plannenService.getPlannenFilters();
      if (filters.discipline || filters.planStatus || filters.planType || filters.infplBeoordeling || filters.organizations) {
        setFilterData(filters);
      }
    }
    catch (error: any) {
      // If token refresh failed, redirect to the logout page
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }

      setError(error.message || 'Er is een fout opgetreden');
      setLoading(false);
    }
  };

  // Function to handle search input change from child component
  const handleSearch = (query: string, from: string, to: string, matchExactRange: boolean, filters: Record<string, any[]>) => {
    setSelectedSearch(query);
    setSelectedStartDate(from);
    setSelectedEndDate(to);
    setMatchExactRange(matchExactRange);
    setSelectedFilters(filters);

    setPageIndex(0);
    refetchPlans(); // Re-fetch plans when search query changes
  };

  const handlePagination = (pageIndex: number, pageSize: number) => {
    setPageIndex(pageIndex);
    setPageSize(pageSize);
    refetchPlans(); // Re-fetch plans when search query changes
  };

  const handleSorting = (field: string, direction: string) => {
    setSortBy(field);
    setSortDirection(direction);
    refetchPlans();
  };

  const handleClickPlan = (planId: string) => {
    if (planId !== routePlanId) {
      navigate(`/plannen/${planId}`);
    }
    setTimeout(() => {
      setPlanId(planId);
    }, 500);
    // console.log('handleOnClickPlan:', planId);
  };

  const handleZoomInOnPlanBbox = (bbox: any) => {
   //  console.log('handleZoomInOnPlanBbox:', bbox);
  };

  const handleClosePlan = () => {
    setPlanId(undefined);
    navigate('/plannen');
  };

  const removeFilterAll = async () => {
    setSelectedSearch('');
    setSelectedStartDate('');
    setSelectedEndDate('');
    setSelectedFilters({
      status: [],
      discipline: [],
      type: [],
      organisatie: [],
      beoordeling: []
    });

    setPageIndex(0);
    refetchPlans(); // Re-fetch plans when search query changes
  };

  const handleFilterChange = (key: string, value: any) => {
    setSelectedFilters((prevFilters) => {
      const currentValues = prevFilters[key] || [];

      const isAlreadySelected = currentValues.includes(value);
      const updatedValues = isAlreadySelected
        ? currentValues.filter((item) => item !== value) // Remove if already selected
        : [...currentValues, value]; // Add if not selected

      return { ...prevFilters, [key]: updatedValues };
    });
    refetchPlans();
  };

  const handleAction = (key: string, value: any, isDelete = false) => {
    switch (key) {
      case 'search':
        setSelectedSearch(isDelete ? '' : value);
        if (isDelete || value === '') {
          refetchPlans();
        }
        break;
      case 'from':
        setSelectedStartDate(isDelete ? '' : value);
        refetchPlans();
        break;
      case 'to':
        setSelectedEndDate(isDelete ? '' : value);
        refetchPlans();
        break;
      case 'matchExactRange':
        setMatchExactRange(value);
        // console.log('Setting matchExactRange to:', value);
        refetchPlans();
        break; 
      case 'all':
        if (isDelete) {
          removeFilterAll();
        }
        else {
          handleFilterChange(key, value);
        }
        break;
      default:
        handleFilterChange(key, value);
        break;
    }
  };

  const handleChange = (key: string, value: any, isDelete: boolean = false) => {
    handleAction(key, value, isDelete);
  };
  
  const handleDelete = (key: string, value: any) => {
    handleAction(key, value, true);
  };

  if (!mijnOrganisatie) {
    return (
      <>
        <NoActiveOrganisatieMessage />  
      </>
    )
  }

  return (
    <>
      <div className="sidebar">
        <div className="container">
          <div className="row">
            <div className="col-1"></div>
            <div className="col-10">
              <FilterPlannen 
                onSearch={handleSearch}
                onChange={handleChange}
                filterData={filterData}
                selectedSearch={selectedSearch}
                selectedStartDate={selectedStartDate}
                selectedEndDate={selectedEndDate}
                matchExactRange={matchExactRange}
                selectedFilters={selectedFilters}
              />
            </div>
            <div className="col-1"></div>
            <div className="col-12">
              <FilterOverview 
                search={selectedSearch} 
                startDate={selectedStartDate} 
                endDate={selectedEndDate}
                matchExactRange={matchExactRange}
                filterData={filterData}
                filters={selectedFilters}
                onDelete={(key: string, value: string) => handleDelete(key, value)}
              />
              <TablePlannen 
                plans={plans} 
                pageIndex={pageIndex}
                pageSize={pageSize}
                totalCount={totalCount}
                panelWidth={panelWidth}
                mijnOrganisatie={mijnOrganisatie}
                onPagination={handlePagination}
                onSorting={handleSorting} 
                onClickPlan={handleClickPlan}
              />
            </div>
          </div>
        </div>

        <div className={`plan-detail ${planId ? "active" : ""}`}>
          { planId && 
            <PlanDetail 
              planId={planId}
              zoomInOnPlanBbox={handleZoomInOnPlanBbox}
              onClosePlan={handleClosePlan}
              refetchPlannen={refetchPlans}
            />
          }
        </div>
      </div>
      
      { loading && (
        <ProgressBar />
      )}
      
    </>
  );
};

export default Sidebar;